import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Users, Mail, Trash2, RefreshCw, Home, FileText, X, CheckCircle, ShieldAlert, Download, Send, FileSpreadsheet, Megaphone, KeyRound, Reply, NotebookText, DollarSign } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { AnnouncementsPanel } from "@/components/AnnouncementsPanel";
import { toast } from "sonner";

const STATUSES = ["new", "contacted", "enrolled", "archived"];
const statusColor = {
  new: "bg-neon-green text-black",
  contacted: "bg-yellow-400 text-black",
  enrolled: "bg-blue-400 text-black",
  archived: "bg-zinc-600 text-white",
};

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();
  const [regs, setRegs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("registrations");
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [waiverView, setWaiverView] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState("");

  const changePassword = async (e) => {
    e.preventDefault();
    setPwError("");
    if (pwForm.next.length < 8) return setPwError("New password must be at least 8 characters.");
    if (pwForm.next !== pwForm.confirm) return setPwError("New passwords do not match.");
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pwForm.current, new_password: pwForm.next });
      toast.success("Password updated. Use it next time you log in.");
      setPwOpen(false);
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      setPwError(formatApiErrorDetail(err.response?.data?.detail) || "Could not update password.");
    } finally {
      setPwSaving(false);
    }
  };

  const [replyView, setReplyView] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replySending, setReplySending] = useState(false);

  const sendReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;
    setReplySending(true);
    try {
      const { data } = await api.post(`/contacts/${replyView.id}/reply`, { message: replyText });
      setContacts((cs) => cs.map((c) => (c.id === data.id ? data : c)));
      toast.success(`Reply emailed to ${replyView.email}.`);
      setReplyView(null);
      setReplyText("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send reply.");
    } finally {
      setReplySending(false);
    }
  };

  const unrepliedCount = contacts.filter((c) => !(c.replies || []).length).length;

  const [notesView, setNotesView] = useState(null);
  const [paySending, setPaySending] = useState(false);

  const sendPayLinks = async () => {
    if (!window.confirm("Email a personal payment link to every unpaid registration?")) return;
    setPaySending(true);
    try {
      const { data } = await api.post("/registrations/payment-requests", { origin_url: window.location.origin });
      toast.success(`Payment links emailed to ${data.sent} of ${data.total} unpaid registration(s).`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send payment links.");
    } finally {
      setPaySending(false);
    }
  };

  const requestPayOne = async (id) => {
    try {
      const { data } = await api.post(`/registrations/${id}/payment-request`, { origin_url: window.location.origin });
      if (data.sent) {
        toast.success("Payment link emailed to the parent.");
        load();
      } else {
        toast.error("Email could not be sent.");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send payment link.");
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, c, s] = await Promise.all([
        api.get("/registrations"),
        api.get("/contacts"),
        api.get("/stats"),
      ]);
      setRegs(r.data);
      setContacts(c.data);
      setStats(s.data);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (ready && !user) navigate("/admin/login");
    if (ready && user) load();
  }, [ready, user, navigate, load]);

  const changeStatus = async (id, status) => {
    const { data } = await api.patch(`/registrations/${id}/status`, { status });
    setRegs((rs) => rs.map((r) => (r.id === id ? data : r)));
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this registration?")) return;
    await api.delete(`/registrations/${id}`);
    setRegs((rs) => rs.filter((r) => r.id !== id));
    load();
  };

  const downloadWaiverPdf = (r) => {
    const w = r.waiver || {};
    const row = (k, v) => `<tr><td style="padding:6px 12px;color:#555;font-size:12px;text-transform:uppercase;letter-spacing:1px;">${k}</td><td style="padding:6px 12px;color:#000;font-size:14px;font-weight:600;">${v || "&mdash;"}</td></tr>`;
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>ARD Waiver - ${w.participant_name || r.player_name}</title>
      <style>
        @media print { .noprint { display:none; } }
        body { font-family: Arial, Helvetica, sans-serif; color:#111; margin:0; padding:40px; }
        h1 { font-size:22px; margin:0 0 4px; letter-spacing:1px; }
        .sub { color:#0a8f2f; font-size:12px; letter-spacing:2px; text-transform:uppercase; font-weight:700; }
        .bar { height:6px; background:#39FF14; margin:16px 0 24px; }
        table { border-collapse:collapse; width:100%; }
        tr { border-bottom:1px solid #eee; }
        h3 { font-size:14px; text-transform:uppercase; margin:18px 0 4px; }
        p.legal { font-size:12px; color:#333; line-height:1.5; margin:0 0 10px; }
        .sig { margin-top:10px; border:1px solid #ccc; max-width:360px; }
        .btn { background:#39FF14; border:none; padding:12px 20px; font-weight:700; text-transform:uppercase; letter-spacing:1px; cursor:pointer; }
      </style></head><body>
      <button class="btn noprint" onclick="window.print()">Download / Print PDF</button>
      <div style="margin-top:20px;">
        <h1>Liability Waiver &amp; Release of Claims</h1>
        <div class="sub">ARD MidSouth Performance &amp; Northpoint Christian School</div>
        <div class="bar"></div>
        <table>
          ${row("Participant", w.participant_name || r.player_name)}
          ${row("Date of Birth", w.date_of_birth)}
          ${row("Age Group", r.age_group)}
          ${row("Parent / Guardian", w.parent_guardian_name)}
          ${row("Phone", w.phone || r.phone)}
          ${row("Email", r.email)}
          ${row("Emergency Contact", w.emergency_contact)}
          ${row("Emergency Phone", w.emergency_contact_phone)}
          ${row("Photo &amp; Media Release", w.photo_release ? "Yes" : "No")}
        </table>
        <h3>Assumption of Risk</h3>
        <p class="legal">Participation in athletic training, strength and conditioning, speed and agility training, fitness instruction, sports performance activities, and related programs conducted by ARD MidSouth Performance at Northpoint Christian School involves inherent risks. The participant voluntarily chooses to participate and knowingly assumes all associated risks.</p>
        <h3>Medical Fitness</h3>
        <p class="legal">The participant certifies they are physically capable of participating and have no medical condition that would make participation unsafe.</p>
        <h3>Release of Liability</h3>
        <p class="legal">In consideration of being allowed to participate, the undersigned, on behalf of themselves and their children, releases, waives, discharges, and holds harmless ARD MidSouth Performance, Northpoint Christian School, and their owners, directors, officers, employees, coaches, trainers, volunteers, agents, and representatives from any and all claims, liabilities, demands, damages, losses, costs, expenses, or causes of action arising out of or relating to any injury, illness, disability, property damage, or other loss sustained while participating, except where prohibited by applicable law.</p>
        <h3>Acknowledgment &amp; Consent</h3>
        <p class="legal">The undersigned has carefully read this agreement, understands its contents, and signs it voluntarily. For participants under 18, the parent/guardian consents to the child's participation and agrees to these terms on behalf of themselves and their child.</p>
        <table style="margin-top:16px;">
          ${row("Printed Name", w.printed_name)}
          ${row("Date Signed", w.signed_date)}
          ${row("Submitted", w.signed_at ? new Date(w.signed_at).toLocaleString() : "")}
        </table>
        <h3>Signature</h3>
        ${w.signature ? `<img class="sig" src="${w.signature}" alt="signature"/>` : "<p class='legal'>No signature on file.</p>"}
      </div>
      <script>window.onload=function(){setTimeout(function(){window.print();},400);}</script>
      </body></html>`;
    const win = window.open("", "_blank");
    if (win) {
      win.document.write(html);
      win.document.close();
    }
  };

  const remindOne = async (id) => {
    try {
      const { data } = await api.post(`/registrations/${id}/remind`);
      toast(data.sent ? "Reminder email sent." : "Could not send reminder.");
      load();
    } catch (e) {
      toast("Could not send reminder.");
    }
  };

  const remindAll = async () => {
    if (!window.confirm("Send a waiver reminder to all parents who haven't signed yet?")) return;
    try {
      const { data } = await api.post(`/registrations/remind-pending`);
      toast(`Reminders sent: ${data.sent} of ${data.total_pending} pending.`);
      load();
    } catch (e) {
      toast("Could not send reminders.");
    }
  };

  const exportCsv = () => {
    const headers = [
      "Player", "Age", "Age Group", "Guardian", "Email", "Phone", "Experience",
      "Notes", "Status", "Waiver Signed", "Reminder Sent", "Photo Release",
      "Emergency Contact", "Emergency Phone", "Date of Birth", "Signed Date", "Created",
    ];
    const esc = (v) => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
    const rows = regs.map((r) => {
      const w = r.waiver || {};
      return [
        r.player_name, r.player_age, r.age_group, r.guardian_name, r.email, r.phone,
        r.experience, r.notes, r.status, r.waiver_signed ? "Yes" : "No",
        r.waiver_reminder_sent ? "Yes" : "No", w.photo_release ? "Yes" : "No",
        w.emergency_contact, w.emergency_contact_phone, w.date_of_birth, w.signed_date, r.created_at,
      ].map(esc).join(",");
    });
    const csv = [headers.map(esc).join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ard-signups-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = regs.filter((r) => {
    if (filter === "all") return true;
    if (filter === "awaiting-waiver") return !r.waiver_signed;
    if (filter === "waiver-signed") return !!r.waiver_signed;
    return r.status === filter;
  });

  if (!ready || (ready && !user)) {
    return <div className="flex min-h-screen items-center justify-center bg-obsidian text-neon-green">Loading…</div>;
  }

  return (
    <div className="grain flex min-h-screen bg-obsidian" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="relative z-10 hidden w-64 flex-col border-r border-white/10 bg-surface p-6 md:flex">
        <Logo />
        <nav className="mt-10 flex flex-col gap-1">
          <button onClick={() => setTab("registrations")} data-testid="tab-registrations" className={`flex items-center gap-3 px-4 py-3 font-body text-sm font-bold uppercase tracking-widest ${tab === "registrations" ? "bg-neon-green text-black" : "text-white/60 hover:text-white"}`}>
            <Users size={18} strokeWidth={2.5} /> Registrations
          </button>
          <button onClick={() => setTab("contacts")} data-testid="tab-contacts" className={`flex items-center gap-3 px-4 py-3 font-body text-sm font-bold uppercase tracking-widest ${tab === "contacts" ? "bg-neon-green text-black" : "text-white/60 hover:text-white"}`}>
            <Mail size={18} strokeWidth={2.5} /> Messages
            {unrepliedCount > 0 && (
              <span data-testid="messages-badge" className={`ml-auto min-w-[22px] px-1.5 py-1 text-center font-heading text-[11px] leading-none ${tab === "contacts" ? "bg-black text-neon-green" : "bg-neon-green text-black"}`}>
                {unrepliedCount}
              </span>
            )}
          </button>
          <button onClick={() => setTab("announcements")} data-testid="tab-announcements" className={`flex items-center gap-3 px-4 py-3 font-body text-sm font-bold uppercase tracking-widest ${tab === "announcements" ? "bg-neon-green text-black" : "text-white/60 hover:text-white"}`}>
            <Megaphone size={18} strokeWidth={2.5} /> Announce
          </button>
        </nav>
        <div className="mt-auto flex flex-col gap-2">
          <button onClick={() => navigate("/")} className="flex items-center gap-3 px-4 py-3 font-body text-xs uppercase tracking-widest text-white/40 hover:text-neon-green"><Home size={16} /> View Site</button>
          <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="logout-btn" className="flex items-center gap-3 px-4 py-3 font-body text-xs uppercase tracking-widest text-white/40 hover:text-red-400"><LogOut size={16} /> Logout</button>
        </div>
      </aside>

      <main className="relative z-10 flex-1 overflow-x-hidden">
        <header className="flex flex-col gap-3 border-b border-white/10 px-6 py-5 sm:flex-row sm:items-center sm:justify-between md:px-10">
          <div>
            <h1 className="font-heading text-3xl uppercase text-white">Dashboard</h1>
            <p className="font-body text-xs uppercase tracking-widest text-white/40">Welcome, {user.name}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={exportCsv} data-testid="export-csv-btn" className="flex items-center gap-2 border border-white/15 px-4 py-2 font-body text-xs uppercase tracking-widest text-white/70 hover:border-neon-green hover:text-neon-green">
              <FileSpreadsheet size={14} /> Export CSV
            </button>
            <button onClick={load} data-testid="refresh-btn" className="flex items-center gap-2 border border-white/15 px-4 py-2 font-body text-xs uppercase tracking-widest text-white/70 hover:border-neon-green hover:text-neon-green">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button onClick={() => { setPwError(""); setPwOpen(true); }} data-testid="change-password-btn" className="flex items-center gap-2 border border-white/15 px-4 py-2 font-body text-xs uppercase tracking-widest text-white/70 hover:border-neon-green hover:text-neon-green">
              <KeyRound size={14} /> Password
            </button>
          </div>
        </header>

        {/* Mobile tab bar (sidebar is desktop-only) */}
        <nav className="flex items-center gap-1 overflow-x-auto border-b border-white/10 bg-surface px-3 py-2 md:hidden" data-testid="mobile-tabs">
          <button onClick={() => setTab("registrations")} data-testid="tab-registrations-mobile" className={`flex items-center gap-2 whitespace-nowrap px-3 py-2.5 font-body text-xs font-bold uppercase tracking-widest ${tab === "registrations" ? "bg-neon-green text-black" : "text-white/60"}`}>
            <Users size={15} strokeWidth={2.5} /> Registrations
          </button>
          <button onClick={() => setTab("contacts")} data-testid="tab-contacts-mobile" className={`flex items-center gap-2 whitespace-nowrap px-3 py-2.5 font-body text-xs font-bold uppercase tracking-widest ${tab === "contacts" ? "bg-neon-green text-black" : "text-white/60"}`}>
            <Mail size={15} strokeWidth={2.5} /> Messages
            {unrepliedCount > 0 && (
              <span data-testid="messages-badge-mobile" className={`min-w-[20px] px-1.5 py-0.5 text-center font-heading text-[11px] leading-none ${tab === "contacts" ? "bg-black text-neon-green" : "bg-neon-green text-black"}`}>
                {unrepliedCount}
              </span>
            )}
          </button>
          <button onClick={() => setTab("announcements")} data-testid="tab-announcements-mobile" className={`flex items-center gap-2 whitespace-nowrap px-3 py-2.5 font-body text-xs font-bold uppercase tracking-widest ${tab === "announcements" ? "bg-neon-green text-black" : "text-white/60"}`}>
            <Megaphone size={15} strokeWidth={2.5} /> Announce
          </button>
          <div className="ml-auto flex items-center gap-1 pl-2">
            <button onClick={() => navigate("/")} data-testid="view-site-mobile" title="View site" className="p-2.5 text-white/40 hover:text-neon-green"><Home size={16} /></button>
            <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="logout-btn-mobile" title="Logout" className="p-2.5 text-white/40 hover:text-red-400"><LogOut size={16} /></button>
          </div>
        </nav>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 gap-px border-b border-white/10 bg-white/10 lg:grid-cols-5">
            {[
              [Users, "Total Sign-Ups", stats.total],
              [CheckCircle, "Waiver Complete", stats.complete],
              [ShieldAlert, "Awaiting Waiver", stats.awaiting_waiver],
              [Users, "By Group", `${stats.by_group["7-9"]} / ${stats.by_group["10-12"]} / ${stats.by_group["13-15"]}`],
              [Mail, "Messages", stats.contacts],
            ].map(([Icon, label, val]) => (
              <div key={label} className={`bg-obsidian p-6 ${label === "Messages" ? "col-span-2 lg:col-span-1" : ""}`}>
                <Icon size={20} strokeWidth={2.5} className={label === "Awaiting Waiver" && val > 0 ? "text-yellow-400" : "text-neon-green"} />
                <div className="mt-4 font-heading text-4xl text-white">{val}</div>
                <div className="mt-1 font-body text-xs uppercase tracking-widest text-white/40">{label}</div>
              </div>
            ))}
          </div>
        )}

        <div className="p-6 md:p-10">
          {tab === "registrations" && (
            <>
              <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap gap-2">
                  {["all", "awaiting-waiver", "waiver-signed", ...STATUSES].map((s) => (
                    <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s}`} className={`border px-4 py-2 font-body text-xs font-bold uppercase tracking-widest ${filter === s ? "border-neon-green bg-neon-green text-black" : "border-white/15 text-white/60 hover:border-white/40"}`}>
                      {s.replace("-", " ")}
                    </button>
                  ))}
                </div>
                <button onClick={sendPayLinks} disabled={paySending} data-testid="pay-links-btn" className="flex items-center gap-2 border border-neon-green/50 px-4 py-2 font-body text-xs font-bold uppercase tracking-widest text-neon-green hover:bg-neon-green hover:text-black disabled:opacity-60">
                  <DollarSign size={14} strokeWidth={2.5} /> {paySending ? "Sending…" : "Email Pay Links"}
                </button>
                <button onClick={remindAll} data-testid="remind-all-btn" className="flex items-center gap-2 border border-yellow-400/50 px-4 py-2 font-body text-xs font-bold uppercase tracking-widest text-yellow-400 hover:bg-yellow-400 hover:text-black">
                  <Send size={14} strokeWidth={2.5} /> Remind Pending
                </button>
              </div>

              <div className="overflow-x-auto border border-white/10" data-testid="registrations-table">
                <table className="w-full min-w-[900px] text-left">
                  <thead>
                    <tr className="border-b border-white/10 bg-surface">
                      {["Player", "Age", "Group", "Guardian", "Contact", "Waiver", "Payment", "Status", ""].map((h) => (
                        <th key={h} className="px-4 py-4 font-body text-xs font-bold uppercase tracking-widest text-white/50">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 && (
                      <tr><td colSpan={9} className="px-4 py-10 text-center font-body text-sm text-white/40">No registrations yet.</td></tr>
                    )}
                    {filtered.map((r) => (
                      <tr key={r.id} data-testid={`reg-row-${r.id}`} className="border-b border-white/5 hover:bg-white/5">
                        <td className="px-4 py-4 font-body text-sm font-bold text-white">
                          {r.player_name}
                          {(r.notes || r.experience) && (
                            <button onClick={() => setNotesView(r)} data-testid={`notes-btn-${r.id}`} className="mt-1 flex items-center gap-1 font-body text-xs font-bold uppercase tracking-wide text-yellow-400 hover:text-neon-green">
                              <NotebookText size={12} strokeWidth={2.5} /> {r.notes ? "Parent Note" : "Experience"}
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-4 font-body text-sm text-white/70">{r.player_age}</td>
                        <td className="px-4 py-4"><span className="border border-neon-green/50 px-2 py-1 font-body text-xs font-bold text-neon-green">{r.age_group}</span></td>
                        <td className="px-4 py-4 font-body text-sm text-white/70">{r.guardian_name}</td>
                        <td className="px-4 py-4 font-body text-xs text-white/60"><div>{r.email}</div><div>{r.phone}</div></td>
                        <td className="px-4 py-4">
                          {r.waiver_signed ? (
                            <button onClick={() => setWaiverView(r)} data-testid={`view-waiver-${r.id}`} className="flex items-center gap-1 bg-neon-green px-2 py-1 font-body text-xs font-bold uppercase text-black">
                              <FileText size={13} strokeWidth={2.5} /> Signed
                            </button>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className="border border-white/15 px-2 py-1 font-body text-xs font-bold uppercase text-white/40">Pending</span>
                              <button onClick={() => remindOne(r.id)} data-testid={`remind-${r.id}`} title="Send waiver reminder" className="flex items-center gap-1 border border-yellow-400/50 px-2 py-1 font-body text-xs font-bold uppercase text-yellow-400 hover:bg-yellow-400 hover:text-black">
                                <Send size={12} strokeWidth={2.5} /> {r.waiver_reminder_sent ? "Re-send" : "Remind"}
                              </button>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          {r.payment_status === "paid" ? (
                            <span data-testid={`paid-${r.id}`} className="flex w-fit items-center gap-1 border border-neon-green/50 bg-neon-green/10 px-2 py-1 font-body text-xs font-bold uppercase text-neon-green">
                              <DollarSign size={12} strokeWidth={2.5} /> Paid
                            </span>
                          ) : (
                            <button onClick={() => requestPayOne(r.id)} data-testid={`pay-link-${r.id}`} title="Email payment link to parent" className="flex items-center gap-1 border border-neon-green/40 px-2 py-1 font-body text-xs font-bold uppercase text-neon-green/80 hover:bg-neon-green hover:text-black">
                              <Send size={12} strokeWidth={2.5} /> {r.payment_requested_at ? "Re-send" : "Pay Link"}
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <select value={r.status} onChange={(e) => changeStatus(r.id, e.target.value)} data-testid={`status-select-${r.id}`} className={`cursor-pointer border-0 px-2 py-1 font-body text-xs font-bold uppercase ${statusColor[r.status]}`}>
                            {STATUSES.map((s) => <option key={s} value={s} className="bg-obsidian text-white">{s}</option>)}
                          </select>
                        </td>
                        <td className="px-4 py-4">
                          <button onClick={() => remove(r.id)} data-testid={`delete-${r.id}`} className="text-white/40 hover:text-red-400"><Trash2 size={16} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {tab === "contacts" && (
            <div className="space-y-4" data-testid="contacts-list">
              {contacts.length === 0 && <p className="font-body text-sm text-white/40">No messages yet. When a parent writes in, you'll be able to reply to them by email right from here.</p>}
              {contacts.map((c) => (
                <div key={c.id} className="border border-white/10 bg-surface p-6">
                  <div className="flex items-center justify-between">
                    <div className="font-heading text-xl uppercase text-white">{c.name}</div>
                    <div className="font-body text-xs text-white/40">{new Date(c.created_at).toLocaleString()}</div>
                  </div>
                  <div className="font-body text-xs text-neon-green">{c.email}</div>
                  <p className="mt-3 font-body text-sm text-white/70">{c.message}</p>
                  {(c.replies || []).length > 0 && (
                    <div className="mt-4 space-y-3 border-l-2 border-neon-green/40 pl-4" data-testid={`replies-${c.id}`}>
                      {c.replies.map((rep, i) => (
                        <div key={i}>
                          <div className="font-body text-xs uppercase tracking-widest text-neon-green/70">
                            You replied · {new Date(rep.sent_at).toLocaleString()}
                          </div>
                          <p className="mt-1 font-body text-sm text-white/60">{rep.message}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    onClick={() => { setReplyView(c); setReplyText(""); }}
                    data-testid={`reply-${c.id}`}
                    className="mt-4 flex items-center gap-2 border border-white/15 px-4 py-2 font-body text-xs uppercase tracking-widest text-white/70 hover:border-neon-green hover:text-neon-green"
                  >
                    <Reply size={14} /> {(c.replies || []).length > 0 ? "Reply Again" : "Reply"}
                  </button>
                </div>
              ))}
            </div>
          )}

          {tab === "announcements" && <AnnouncementsPanel />}
        </div>
      </main>

      {waiverView && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/80 p-4 backdrop-blur-sm" data-testid="waiver-modal" onClick={() => setWaiverView(null)}>
          <div className="relative my-8 w-full max-w-2xl border border-white/15 bg-surface p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setWaiverView(null)} data-testid="close-waiver-modal" className="absolute right-4 top-4 text-white/50 hover:text-neon-green"><X size={22} strokeWidth={2.5} /></button>
            <div className="flex items-center gap-3 border-b border-white/10 pb-4">
              <FileText size={22} strokeWidth={2.5} className="text-neon-green" />
              <div>
                <h3 className="font-heading text-2xl uppercase leading-none text-white">Signed Waiver</h3>
                <p className="font-body text-xs uppercase tracking-widest text-neon-green">{waiverView.player_name}</p>
              </div>
            </div>
            <button
              onClick={() => downloadWaiverPdf(waiverView)}
              data-testid="download-waiver-pdf"
              className="mt-4 flex items-center gap-2 bg-neon-green px-5 py-3 font-heading text-sm uppercase tracking-widest text-black hover:opacity-90"
            >
              <Download size={16} strokeWidth={2.5} /> Download PDF
            </button>
            {(() => {
              const w = waiverView.waiver || {};
              const rows = [
                ["Participant", w.participant_name],
                ["Date of Birth", w.date_of_birth],
                ["Parent / Guardian", w.parent_guardian_name || "—"],
                ["Phone", w.phone],
                ["Emergency Contact", w.emergency_contact],
                ["Emergency Phone", w.emergency_contact_phone],
                ["Photo & Media Release", w.photo_release ? "Yes" : "No"],
                ["Printed Name", w.printed_name],
                ["Date Signed", w.signed_date],
                ["Submitted", w.signed_at ? new Date(w.signed_at).toLocaleString() : "—"],
              ];
              return (
                <div className="mt-5 space-y-3">
                  <table className="w-full text-left">
                    <tbody>
                      {rows.map(([k, v]) => (
                        <tr key={k} className="border-b border-white/5">
                          <td className="py-2 pr-4 font-body text-xs uppercase tracking-widest text-white/40">{k}</td>
                          <td className="py-2 font-body text-sm text-white">{v}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div>
                    <div className="mb-2 font-body text-xs uppercase tracking-widest text-white/40">Signature</div>
                    {w.signature ? (
                      <img src={w.signature} alt="signature" className="w-full max-w-sm border border-white/15 bg-zinc-900" data-testid="waiver-signature-img" />
                    ) : (
                      <span className="font-body text-sm text-white/40">No signature on file</span>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Change password modal */}
      {pwOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-5" onClick={() => setPwOpen(false)}>
          <form onSubmit={changePassword} onClick={(e) => e.stopPropagation()} data-testid="change-password-modal" className="w-full max-w-md border border-white/10 bg-surface p-8">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-2xl uppercase text-white">Change Password</h3>
              <button type="button" onClick={() => setPwOpen(false)} data-testid="change-password-close" className="text-white/40 hover:text-white"><X size={20} /></button>
            </div>
            <p className="mt-2 font-body text-xs text-white/50">Updating password for <span className="text-neon-green">{user.email}</span></p>
            {pwError && (
              <div data-testid="change-password-error" className="mt-4 border border-red-500/50 bg-red-500/10 px-4 py-3 font-body text-sm text-red-300">{pwError}</div>
            )}
            <div className="mt-6 space-y-4">
              <div>
                <label className="mb-2 block font-body text-xs font-bold uppercase tracking-[0.2em] text-neon-green/80">Current Password</label>
                <input required type="password" data-testid="cp-current" value={pwForm.current} onChange={(e) => setPwForm((f) => ({ ...f, current: e.target.value }))} className="w-full rounded-none border border-white/15 bg-zinc-900 px-4 py-3 font-body text-sm text-white outline-none focus:border-neon-green" />
              </div>
              <div>
                <label className="mb-2 block font-body text-xs font-bold uppercase tracking-[0.2em] text-neon-green/80">New Password</label>
                <input required type="password" data-testid="cp-new" value={pwForm.next} onChange={(e) => setPwForm((f) => ({ ...f, next: e.target.value }))} className="w-full rounded-none border border-white/15 bg-zinc-900 px-4 py-3 font-body text-sm text-white outline-none focus:border-neon-green" />
              </div>
              <div>
                <label className="mb-2 block font-body text-xs font-bold uppercase tracking-[0.2em] text-neon-green/80">Confirm New Password</label>
                <input required type="password" data-testid="cp-confirm" value={pwForm.confirm} onChange={(e) => setPwForm((f) => ({ ...f, confirm: e.target.value }))} className="w-full rounded-none border border-white/15 bg-zinc-900 px-4 py-3 font-body text-sm text-white outline-none focus:border-neon-green" />
              </div>
            </div>
            <button type="submit" disabled={pwSaving} data-testid="cp-submit" className="mt-6 flex w-full items-center justify-center bg-neon-green py-3 font-heading text-sm uppercase tracking-widest text-black transition-opacity disabled:opacity-60">
              {pwSaving ? "Saving…" : "Update Password"}
            </button>
          </form>
        </div>
      )}

      {/* Reply-to-message modal */}
      {replyView && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-5" onClick={() => setReplyView(null)}>
          <form onSubmit={sendReply} onClick={(e) => e.stopPropagation()} data-testid="reply-modal" className="w-full max-w-lg border border-white/10 bg-surface p-8">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-2xl uppercase text-white">Reply</h3>
              <button type="button" onClick={() => setReplyView(null)} data-testid="reply-close" className="text-white/40 hover:text-white"><X size={20} /></button>
            </div>
            <p className="mt-2 font-body text-xs text-white/50">
              To <span className="text-neon-green">{replyView.name}</span> ({replyView.email}) — sent as an email from ARD Midsouth Performance.
            </p>
            <div className="mt-4 border border-white/10 bg-obsidian p-4">
              <div className="font-body text-xs uppercase tracking-widest text-white/40">Their message</div>
              <p className="mt-1 font-body text-sm text-white/60">{replyView.message}</p>
            </div>
            <textarea
              required
              rows={5}
              data-testid="reply-text"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Write your reply…"
              className="mt-4 w-full rounded-none border border-white/15 bg-zinc-900 px-4 py-3 font-body text-sm text-white placeholder-white/30 outline-none focus:border-neon-green"
            />
            <button type="submit" disabled={replySending} data-testid="reply-send" className="mt-4 flex w-full items-center justify-center gap-2 bg-neon-green py-3 font-heading text-sm uppercase tracking-widest text-black transition-opacity disabled:opacity-60">
              {replySending ? "Sending…" : <><Send size={16} strokeWidth={2.5} /> Send Reply</>}
            </button>
          </form>
        </div>
      )}

      {/* Parent notes modal */}
      {notesView && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-5" onClick={() => setNotesView(null)}>
          <div data-testid="notes-modal" onClick={(e) => e.stopPropagation()} className="w-full max-w-md border border-white/10 bg-surface p-8">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-2xl uppercase text-white">{notesView.player_name}</h3>
              <button type="button" onClick={() => setNotesView(null)} data-testid="notes-close" className="text-white/40 hover:text-white"><X size={20} /></button>
            </div>
            <p className="mt-1 font-body text-xs text-white/50">What the parent shared at sign-up</p>
            <div className="mt-5 space-y-5">
              <div>
                <p className="font-body text-xs font-bold uppercase tracking-[0.2em] text-neon-green/80">Playing Experience</p>
                <p data-testid="notes-experience" className="mt-1 font-body text-sm text-white/70">{notesView.experience || "—"}</p>
              </div>
              <div>
                <p className="font-body text-xs font-bold uppercase tracking-[0.2em] text-neon-green/80">Notes About Their Child</p>
                <p data-testid="notes-body" className="mt-1 whitespace-pre-wrap font-body text-sm text-white/70">{notesView.notes || "No notes written."}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
